'use client';

/**
 * MetricTile - Displays analytics/metrics search results
 *
 * Shows metric information including:
 * - Metric name and value
 * - Trend indicator (up/down/stable)
 * - Percentage change
 * - Time period
 * - Visual sparkline (optional)
 */

import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Target,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { createTile, type BaseTileRenderProps } from './BaseTile';
import type { MetricResult } from '../types';

/**
 * Format large numbers
 */
function formatNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString();
}

/**
 * Format percentage change
 */
function formatChange(change?: number): string {
  if (change === undefined) return '';
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(1)}%`;
}

/**
 * Trend indicator component
 */
function TrendIndicator({
  trend,
  change,
}: {
  trend?: MetricResult['metadata']['trend'];
  change?: number;
}) {
  if (!trend) return null;

  const config = {
    up: {
      icon: TrendingUp,
      className: 'text-emerald-400',
      bgClassName: 'bg-emerald-500/10 border-emerald-500/30',
    },
    down: {
      icon: TrendingDown,
      className: 'text-red-400',
      bgClassName: 'bg-red-500/10 border-red-500/30',
    },
    stable: {
      icon: Minus,
      className: 'text-slate-400',
      bgClassName: 'bg-slate-500/10 border-slate-500/30',
    },
  };

  const cfg = config[trend];
  const Icon = cfg.icon;

  return (
    <div className={cn('flex items-center gap-1.5 px-2 py-1 rounded-md border', cfg.bgClassName)}>
      <Icon className={cn('w-4 h-4', cfg.className)} />
      {change !== undefined && (
        <span className={cn('text-sm font-medium tabular-nums', cfg.className)}>
          {formatChange(change)}
        </span>
      )}
    </div>
  );
}

/**
 * Metric value display
 */
function MetricValue({
  value,
  unit,
}: {
  value: number;
  unit?: string;
}) {
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-2xl font-bold text-indigo-400 tabular-nums">
        {formatNumber(value)}
      </span>
      {unit && (
        <span className="text-sm text-slate-500">{unit}</span>
      )}
    </div>
  );
}

/**
 * Simple sparkline visualization
 */
function MiniSparkline() {
  // Placeholder - could be replaced with actual data visualization
  const bars = [40, 65, 45, 80, 60, 90, 75];

  return (
    <div className="flex items-end gap-0.5 h-6">
      {bars.map((height, i) => (
        <div
          key={i}
          className="w-1 bg-indigo-500/40 rounded-sm"
          style={{ height: `${height}%` }}
        />
      ))}
    </div>
  );
}

/**
 * MetricTile component
 */
export const MetricTile = createTile<MetricResult>(
  'MetricTile',
  (result) => {
    const { metadata } = result;

    const renderProps: BaseTileRenderProps = {
      icon: BarChart3,
      iconColor: 'text-indigo-400',
      title: result.title,
      subtitle: metadata.period,
      preview: result.preview,
      metadata: [],
      tags: [],
      rightContent: (
        <div className="flex items-center gap-3">
          <MetricValue value={metadata.value} unit={metadata.unit} />
          <TrendIndicator trend={metadata.trend} change={metadata.change} />
        </div>
      ),
    };

    // Add period as metadata
    if (metadata.period) {
      renderProps.metadata!.push({
        label: 'Period',
        value: metadata.period,
        icon: Calendar,
      });
    }

    // Add sparkline visualization in expanded mode
    renderProps.bottomContent = (
      <div className="flex items-center justify-between mt-2 p-2 bg-slate-800/30 rounded-md border border-slate-700/50">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Target className="w-3 h-3" />
          <span>Trend over time</span>
        </div>
        <MiniSparkline />
      </div>
    );

    return renderProps;
  }
);

export default MetricTile;
